/**
 * Shapes returned by the CRM's OCR proxy (`POST ocr/documents/process`).
 *
 * The proxy forwards the OCR pipeline's report verbatim, wrapped with a
 * request id - it is NOT the flattened single-record shape the integration
 * notes described. The real payload is:
 *
 *   { request_id, records: [ ... ], scans: [ ... ], stats: { ... } }
 *
 * The property that matters most in this contract is that a value's
 * trustworthiness is carried by `validated`, NOT by `confidence`. A national
 * number whose mod-11 check digit verified arrives with `validated: true` and
 * a confidence around 0.73, while an Arabic name that three passes agreed on
 * may sit at 0.12 and can never be validated at all, because no checksum for
 * it exists. Ranking by confidence alone would rank a proven number below a
 * guessed one.
 */

/** A field as it appears in `identity` - the consolidated, cross-face view. */
export interface OcrField {
  /** null when the field could not be read. */
  value: string | null;
  /** 0-1. Useful for ordering within a category, not for deciding trust. */
  confidence: number;
  /** Which face produced it: id_front | id_back | passport_mrz. */
  from?: string | null;
}

/**
 * A field as it appears inside `id_front` / `id_back`. Only this view carries
 * the evidence, which is why trust has to be resolved through it.
 */
export interface OcrFaceField {
  value: string | null;
  confidence: number;
  /** ocr | ocr+checksum | ocr+lexicon | derived | mrz | barcode */
  source?: string | null;
  /** Passes that produced this value over passes that produced any, e.g. "3/3". */
  agreement?: string | null;
  /** Something independent confirmed it - a check digit, an MRZ composite. */
  validated?: boolean;
  /** Other readings, kept rather than silently discarded. */
  alternatives?: string[];
  notes?: string[];
}

export type OcrFlagSeverity = 'critical' | 'warning' | 'info';

export interface OcrFlag {
  severity: OcrFlagSeverity;
  code: string;
  message: string;
}

export interface OcrIdentity {
  national_number?: OcrField;
  birth_date?: OcrField;
  governorate?: OcrField;
  gender?: OcrField;
  marital_status?: OcrField;
  religion?: OcrField;
  full_name_ar?: OcrField;
  first_name_ar?: OcrField;
  address_ar?: OcrField;
  occupation_ar?: OcrField;
  id_serial?: OcrField;
  id_issue_date?: OcrField;
  id_expiry_date?: OcrField;
  [key: string]: OcrField | undefined;
}

/** One person, with both faces merged. This is what a caller consumes. */
export interface OcrRecord {
  /** `nid:<number>` once the number is confirmed, otherwise `sha256:<hash>`. */
  key: string | null;
  national_number?: string | null;
  identity: OcrIdentity;
  needs_review: boolean;
  flags: OcrFlag[];
  id_front?: { [field: string]: OcrFaceField } | null;
  id_back?: { [field: string]: OcrFaceField } | null;
  passports?: any[];
  sources?: string[];
}

export interface OcrStats {
  files_seen?: number;
  duplicates_skipped?: number;
  images_processed?: number;
  errors?: number;
  records?: number;
  records_needing_review?: number;
  elapsed_seconds?: number;
}

/** The full envelope the proxy returns. */
export interface OcrProcessResponse {
  request_id?: string;
  records: OcrRecord[];
  scans?: any[];
  stats?: OcrStats;
}

/**
 * Identity keys are named for the consolidated record; the per-face field they
 * came from is often called something else. Resolving `validated` means
 * translating across this map, exactly as the pipeline's own flat endpoints do.
 */
export const OCR_IDENTITY_TO_FACE_FIELD: { [identityKey: string]: string } = {
  full_name_ar: 'full_name',
  first_name_ar: 'first_name',
  address_ar: 'address',
  occupation_ar: 'occupation',
  id_serial: 'serial',
  id_issue_date: 'issue_date',
  id_expiry_date: 'expiry_date',
};

/**
 * The pipeline's review threshold. Retained for display ordering only - never
 * use it to decide whether a value can be trusted. `validated` decides that.
 */
export const OCR_REVIEW_THRESHOLD = 0.7;



// WEBPACK FOOTER //
// ./src/app/models/ocr.model.ts